import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SortableTable from '../../../../components/common/SortableTable/SortableTable.jsx';
import Pagination from '../../../../components/common/Pagination/Pagination.jsx';
import ConfirmationModal from '../../../../components/ui/ConfirmationModal/ConfirmationModal.jsx';
import { useConfirmation } from '../../../../hooks/useConfirmation';
import { formatCurrency } from '../../../../utils/formatters';

// Separate search input component to isolate re-renders
const SearchInput = React.memo(({ value, onChange, placeholder }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="search-input"
    />
));

// Separate action button to prevent re-renders
const ActionButton = React.memo(({ onClick, text, disabled }) => (
    <button 
        onClick={onClick}
        className="btn btn-primary"
        disabled={disabled}
    >
        {text}
    </button>
));

// Loading overlay component
const LoadingOverlay = React.memo(({ loading }) => 
    loading ? (
        <div className="table-loading-overlay">
            <div className="spinner"></div>
            <span>Loading...</span>
        </div>
    ) : null
);

const ProductTable = React.memo(({
    hookData,
    onAddProduct,
    onEditProduct,
    onAdjustStock
}) => {
    const { t } = useTranslation(['inventory', 'common']);
    const { isOpen, confirmationConfig, confirm, closeConfirmation } = useConfirmation();
    const [deletingItemId, setDeletingItemId] = useState(null);

    // Table columns configuration with proper memoization
    const columns = useMemo(() => [
        { key: 'name', label: t('inventory:name'), sortable: true },
        { key: 'sku', label: t('inventory:sku'), sortable: true },
        { key: 'category_name', label: t('inventory:category'), sortable: true },
        { key: 'supplier_name', label: t('inventory:supplier'), sortable: true },
        {
            key: 'quantity_in_stock',
            label: t('inventory:stock'),
            sortable: true,
            render: (product) => (
                <span className={product.quantity_in_stock <= product.minimum_stock_level ? 'stock-warning' : ''}>
                    {product.quantity_in_stock}
                    {product.quantity_in_stock === 0 && ' ❌'}
                    {product.quantity_in_stock > 0 && product.quantity_in_stock <= product.minimum_stock_level && ' ⚠️'}
                </span>
            )
        },
        {
            key: 'unit_price',
            label: t('inventory:unitPrice'),
            sortable: true,
            render: (product) => formatCurrency(product.unit_price)
        },
        {
            key: 'is_active',
            label: t('inventory:status'),
            sortable: true,
            render: (product) => (
                <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                    {product.is_active ? t('common:active') : t('common:inactive')}
                </span>
            )
        }
    ], [t]);

    const getRowClassName = useCallback((product) => {
        if (product.quantity_in_stock === 0) return 'out-of-stock';
        if (product.quantity_in_stock <= product.minimum_stock_level) return 'low-stock';
        return '';
    }, []);

    // Handle delete with confirmation
    const handleDeleteClick = useCallback(async (product) => {
        const confirmed = await confirm({
            title: t('common:confirmation'),
            message: t('inventory:confirmDeleteProduct', { name: product.name }),
            confirmText: t('common:buttons.delete'),
            confirmButtonClass: 'btn-danger'
        });

        if (confirmed) {
            setDeletingItemId(product.id);
            await hookData.deleteProduct(product.id);
            setDeletingItemId(null);
        }
    }, [confirm, t, hookData]);

    // Transform data for table display with memoization
    const transformedData = useMemo(() => {
        return hookData.products.map(product => ({
            ...product,
            category_name: product.category_name || '-',
            supplier_name: product.supplier_name || '-'
        }));
    }, [hookData.products]);

    // Memoized pagination props
    const paginationProps = useMemo(() => ({
        pagination: hookData.pagination,
        onPageChange: hookData.onPageChange,
        onLimitChange: hookData.onLimitChange,
        limitOptions: hookData.limitOptions
    }), [
        hookData.pagination,
        hookData.onPageChange,
        hookData.onLimitChange,
        hookData.limitOptions
    ]);

    return (
        <div className="inventory-content">
            {/* Search and Action Bar - isolated components */}
            <div className="action-bar">
                <SearchInput
                    value={hookData.search}
                    onChange={hookData.onSearchChange}
                    placeholder={t('inventory:searchProducts')}
                />
                <ActionButton
                    onClick={onAddProduct}
                    text={`${t('common:add')} ${t('inventory:product')}`}
                    disabled={hookData.actionLoading}
                />
            </div>

            {/* Table with loading overlay - isolated re-renders */}
            <div style={{ position: 'relative' }}>
                <SortableTable
                    columns={columns}
                    data={transformedData}
                    onEdit={onEditProduct}
                    onDelete={handleDeleteClick}
                    onSpecialAction={onAdjustStock}
                    specialActionText={t('inventory:adjust')}
                    getRowClassName={getRowClassName}
                    sortField={hookData.sort}
                    sortOrder={hookData.order}
                    onSort={hookData.onSortChange}
                    emptyMessage={hookData.search ? t('inventory:noProductsSearch') : t('inventory:noProductsFound')}
                />
                <LoadingOverlay loading={hookData.loading} />
            </div>

            {/* Pagination Controls - memoized */}
            {hookData.pagination && (
                <Pagination {...paginationProps} />
            )}

            <ConfirmationModal
                isOpen={isOpen}
                onClose={closeConfirmation}
                onConfirm={confirmationConfig.onConfirm}
                title={confirmationConfig.title}
                message={confirmationConfig.message}
                confirmText={confirmationConfig.confirmText}
                cancelText={confirmationConfig.cancelText}
                confirmButtonClass={confirmationConfig.confirmButtonClass}
                loading={deletingItemId !== null}
            />
        </div>
    );
});

// Display name for React DevTools
ProductTable.displayName = 'ProductTable';

export default ProductTable;